import {forEach} from "lodash/fp";
import root from "window-or-global";

import {trackWebViewEvent} from "@atg-mobileapp-shared/analytics";
import {trackOptimizelyEvent} from "@atg-shared/feature-experimentation";
import {
    currentTrackingSystem,
    isCasinoApp,
    isHorseApp,
    isSportApp,
} from "@atg-shared/system";

import type {TrackingEvent, TrackingVar} from "@atg-shared/analytics-types";

const isCamelCase = (str: string) => /^[a-z][a-zA-Z0-9]+$/.test(str);

const isAppWebView = isSportApp || isCasinoApp || isHorseApp;

export type TrackEvent<T extends TrackingEvent = TrackingEvent> = (eventData: T) => void;

// Helper function to validate event data in development
const validateEventData = (eventData: TrackingEvent) => {
    if (process.env.NODE_ENV !== "production") {
        // Check for required event property
        if (!eventData.event) {
            throw new Error(
                `Required property "event" missing in ${JSON.stringify(
                    eventData,
                    null,
                    2,
                )}`,
            );
        }

        // Validate event name format
        if (!isCamelCase(eventData.event)) {
            throw new Error(
                `Expected event name (VALUE) to be in camelCase, got "${eventData.event}".`,
            );
        }

        // Validate all property keys
        // @ts-expect-error suppressed error for Flow to TS migration
        forEach.convert({cap: false})((val, key) => {
            if (!/^[a-z][a-zA-Z0-9]+$/.test(key)) {
                throw new Error(
                    `Expected event property (KEY) to be in camelCase, got "${key}".`,
                );
            }
        }, eventData);
    }
};

// Helper function to check if GTM is loaded
const isGtmLoaded = () => {
    if (!root.dataLayer) return false;
    if (process.env.NODE_ENV === "test") return true;

    return root.dataLayer.some(
        (item) =>
            typeof item === "object" &&
            item !== null &&
            "event" in item &&
            item.event === "gtm.load",
    );
};

// Helper function to handle retry logic
const retryEventPush = (eventData: TrackingEvent) => {
    const maxRetries = 10;
    const retryInterval = 500;
    let retryCount = 0;

    const attemptPush = () => {
        if (isGtmLoaded()) {
            root.dataLayer.push(eventData);
            return;
        }

        retryCount += 1;

        if (retryCount >= maxRetries) {
            // GTM not loaded after max retries. Pushing event anyway and hoping for the best.
            root.dataLayer.push(eventData);
            return;
        }

        setTimeout(attemptPush, retryInterval);
    };

    attemptPush();
};

/**
 * Send an event to GTM, by pushing it to `window.dataLayer`. Must contain the required `event`
 * property, which has special meaning in GTM.
 *
 * If you want to set a global tracking variable without tying it to a specific event, use the
 * `trackVariable` function instead.
 *
 * This method is used both in the web, in React Native app, and in its webviews. However all our analytics is done via atg.se.
 *
 * When used on the web it is enough to push the events to dataLayer.
 *
 * When used in the React Native app we send the analytics events via special postAnalytics method to atg.se in DepositModal webview.
 * Thus even if in this function we just push to dataLayer, in React Native the dataLayer.push function is overridden to postMessage the events to the webview.
 *
 * When used in the webviews, the chain is: postMessage to RN host, then postMessage to atg.se.
 */
export const trackEvent: TrackEvent = (eventData) => {
    validateEventData(eventData);

    trackOptimizelyEvent(eventData);

    /**
     * We can run web based solutions within the native app and when this runs we need to send
     * the events through the native bridge with postMessage.
     */
    if (isAppWebView) {
        trackWebViewEvent(eventData);
        return;
    }

    // need to add origin to prevent multiple Optimizely events being fired
    const _eventOrigin = currentTrackingSystem();

    // prevent overriding origin if it already exists
    const eventWithOrigin: TrackingEvent = eventData._eventOrigin
        ? eventData
        : {
              ...eventData,
              _eventOrigin,
          };

    root.dataLayer = root.dataLayer || [];

    if (isGtmLoaded()) {
        root.dataLayer.push(eventWithOrigin);
        return;
    }

    retryEventPush(eventWithOrigin);
};

/**
 * Set a global GTM variable, by pushing it to `window.dataLayer`. Must **not** contain an `event`
 * property, which has special meaning in GTM.
 *
 * If you want to trigger an event, use the `trackEvent` function instead.
 */
export const trackVariable = (variableData: TrackingVar) => {
    // To a large extent our GTM integration is based on name conventions. By being strict about our
    // naming it becomes easier to find the relevant events and variables in the GTM web admin.
    if (process.env.NODE_ENV !== "production") {
        if (variableData.event) {
            throw new Error(
                `Prohibited property "event" exists in in ${JSON.stringify(
                    variableData,
                    null,
                    2,
                )}`,
            );
        }

        // @ts-expect-error: suppressed error during Flow to TS migration
        forEach.convert({cap: false})((val, key) => {
            if (!isCamelCase(key)) {
                throw new Error(
                    `Expected variable property (KEY) to be in camelCase, got "${key}".`,
                );
            }
        }, variableData);
    }

    if (root.dataLayer) {
        root.dataLayer.push(variableData);
    }
};
